import { DebugReporter } from './debugReporter';
import type { IReporter } from './IReporter';
import type { RunnerOptions } from '../runOptions';
import type { TestRunStatusTestResult } from '../testRunStatus';

class Reporter implements Required<IReporter> {
    reporters: IReporter[] = [];
    onGetBrowserFailure = this.generateHook('onGetBrowserFailure');
    onGetBrowserSuccess = this.generateHook('onGetBrowserSuccess');
    onTestPlanStarted = this.generateHook('onTestPlanStarted');
    onGetSlot = this.generateHook('onGetSlot');
    onGetSession = this.generateHook('onGetSession');
    onTestFinished = this.generateHook('onTestFinished');
    onTestFailed = this.generateHook('onTestFailed');
    onTestPassed = this.generateHook('onTestPassed');
    onTestStarted = this.generateHook('onTestStarted');
    onTestIgnored = this.generateHook('onTestIgnored');
    onWaitToTestStart = this.generateHook('onWaitToTestStart');
    onWaitToTestComplete = this.generateHook('onWaitToTestComplete');
    onTestPlanFinished = this.generateHook('onTestPlanFinished', this.onTestPlanFinishedMutator);
    onAllTestPlansFinished = this.generateHook('onAllTestPlansFinished', this.onAllTestPlansFinishedMutator);

    public async setOptions(options: RunnerOptions, branchToUse: string) {
        this.reporters = [];
        const optReporters = options.reporters;
        this.reporters.push(new DebugReporter(options));

        if (optReporters === undefined || optReporters.length === 0) {
            const { Reporter: ConsoleReporter } = await import('./consoleReporter');
            this.reporters.push(new ConsoleReporter(options, branchToUse));
            if (options?.reportFile !== undefined) {
                const { Reporter: JunitReporter } = await import('./junitReporter');
                this.reporters.push(new JunitReporter(options, branchToUse));
            }
        } else {
            const reporterModules = await Promise.all([
                optReporters.includes('teamcity') && import('./teamCityReporter'),
                optReporters.includes('console') && import('./consoleReporter'),
                optReporters.includes('junit') && import('./junitReporter'),
                optReporters.includes('json') && import('./jsonReporter'),
                optReporters.includes('chrome') && import('./chromeReporter'),
            ]);
            reporterModules.forEach((reporterModule) => {
                if (!reporterModule) {
                    return;
                }
                this.reporters.push(new reporterModule.Reporter(options, branchToUse));
            });
        }
    }

    private onTestPlanFinishedMutator(
        testResults: { [resultId: string]: TestRunStatusTestResult },
        testPlanName: string,
        startTime: number,
        executionId: string,
        isAnonymous: boolean | undefined,
    ) {
        let results: { [resultId: string]: any } = {};

        results = testResults;
        const duration = Date.now() - (startTime || 0);
        return [results, testPlanName, duration, executionId, isAnonymous] as Parameters<Required<IReporter>['onTestPlanFinished']>;
    }

    private onAllTestPlansFinishedMutator(testPlanResults: Parameters<Required<IReporter>['onAllTestPlansFinished']>[0]) {
        return [testPlanResults] as Parameters<Required<IReporter>['onAllTestPlansFinished']>;
    }

    private generateHook<T extends keyof IReporter>(name: T, preHookMutator?: (...args: [...Parameters<Required<IReporter>[T]>]) => [...Parameters<Required<IReporter>[T]>]) {
        return async (...args: [...Parameters<Required<IReporter>[T]>]) => {
            const data = preHookMutator?.(...args);
            for (const reporter of this.reporters) {
                // @ts-expect-error TS is unable to figure out what is happening here
                await reporter[name]?.(...(data || args));
            }
        };
    }
}

export const reporter = new Reporter();
